import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  useTheme,
} from '@mui/material';
import { DotsLoader } from 'Components/DotsLoader';
import { ButtonForm } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';
import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUsersGroups } from 'services/users-groups-service';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getStyles = (id, selectedIds = [], theme) => ({
  fontWeight: selectedIds.includes(id)
    ? theme.typography.fontWeightMedium
    : theme.typography.fontWeightRegular,
});

export const Details = ({
  register,
  control,
  isLoading,
  errors,
  handleComplete,
  handleSubmit,
}) => {
  const { t } = useTranslation(['policy']);
  const selectedTenant = useSelector(useAuthTenant);
  const theme = useTheme();
  const navigate = useNavigate();

  const { data: usersGroups } = useQuery(['getUsersGroups'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersGroups(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const byId = useMemo(() => {
    const m = new Map();
    (usersGroups ?? []).forEach(g => m.set(g._id, g));
    return m;
  }, [usersGroups]);

  return (
    <form onSubmit={handleSubmit(handleComplete)}>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          <TextField
            {...register('name', { required: t('policy:form.name.required') })}
            label={t('policy:form.name.label')}
            autoFocus
            error={!!errors?.name}
            sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
            helperText={errors?.name ? errors.name.message : null}
            InputLabelProps={{ shrink: true }}
          />
        </Column>
      </Row>
      <Row>
        <Column xs={12} className="d-flex">
          <TextField
            {...register('classification', { required: t('policy:form.classification.required') })}
            label={t('policy:form.classification.label')}
            error={!!errors?.classification}
            sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
            helperText={errors?.classification ? errors.classification.message : null}
            InputLabelProps={{ shrink: true }}
          />
        </Column>
      </Row>
      <Row justifyContent="center">
        <Column xs={12} className="d-flex">
          <Controller
            control={control}
            name="userGroups"
            render={({ field: { onChange, value = [] }, fieldState }) => {
              const handleSelectChange = (event) => {
                const v = event.target.value;
                const next = Array.isArray(v)
                  ? v
                  : typeof v === 'string'
                  ? v.split(',')
                  : [];
                onChange(Array.from(new Set(next)));
              };

              const handleDelete = (idToRemove) => {
                onChange(value.filter((id) => id !== idToRemove));
              };

              return (
                <FormControl fullWidth sx={{ margin: '0 auto 24px' }} error={!!fieldState.error}>
                  <InputLabel id="user-groups-label" shrink>
                    {t('policy:form.userGroups.label')}
                  </InputLabel>

                  <Select
                    labelId="user-groups-label"
                    id="userGroups"
                    multiple
                    value={value}
                    onChange={handleSelectChange}
                    notched
                    input={<OutlinedInput id="user-groups-chip" label={t('policy:form.userGroups.label')} />}
                    renderValue={(selectedIds) => (
                      <Box
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {(selectedIds || [])
                          .map((id) => byId.get(id))
                          .filter(Boolean)
                          .map((g) => (
                            <Chip
                              key={g._id}
                              label={g.name}
                              variant="outlined"
                              color="primary"
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleDelete(g._id);
                              }}
                              sx={{ width: 'fit-content' }}
                            />
                          ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {(usersGroups ?? []).map((item) => (
                      <MenuItem
                        key={item._id}
                        value={item._id}
                        style={getStyles(item._id, value, theme)}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>{fieldState.error?.message ?? ''}</FormHelperText>
                </FormControl>
              );
            }}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <TextField
            {...register('description', { required: t('policy:form.description.required') })}
            label={t('policy:form.description.label')}
            multiline
            rows={4}
            error={!!errors?.description}
            helperText={errors?.description ? errors.description.message : null}
            sx={{ margin: '0 auto 24px', width: '100%' }}
            InputLabelProps={{ shrink: true }}
          />
        </Column>
      </Row>
      <Row justifyContent="end" alignItems="center">
        <Column xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <ButtonForm
              secondary
              id="cancel"
              label={t('policy:form.cancel')}
              onClick={() => navigate('/policies')}
            />
            <ButtonForm
              type="submit"
              label={isLoading ? <DotsLoader /> : t('policy:form.save')}
              disabled={isLoading}
            />
          </Box>
        </Column>
      </Row>
    </form>
  );
};
